import { useEffect, useRef } from 'react';

interface HeatmapPoint {
  x: number;
  y: number;
  intensity: number;
}

interface HeatmapViewerProps {
  data: HeatmapPoint[];
  pagePath: string;
}

// Mapeamento de screenshots das páginas
const pageScreenshots: Record<string, string> = {
  '/': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==', // placeholder
  '/demo': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==', // placeholder
  '/mapa': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==' // placeholder
};

export const HeatmapViewer = ({ data, pagePath }: HeatmapViewerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
  
  // Calcular altura dinâmica baseada nos dados
  const dynamicHeight = data.length > 0 
    ? Math.max(...data.map(p => p.y), 1200) + 200 
    : 1200;
  
  // Desenhar background da página
  useEffect(() => {
    if (!backgroundCanvasRef.current || !containerRef.current) return;
    
    const canvas = backgroundCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const container = containerRef.current;
    canvas.width = container.offsetWidth;
    canvas.height = dynamicHeight;
    
    // Fundo claro
    ctx.fillStyle = 'rgb(250, 250, 250)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Grid de coordenadas melhorado
    ctx.lineWidth = 1;
    
    // Linhas verticais
    for (let x = 0; x < canvas.width; x += 100) {
      ctx.strokeStyle = x % 500 === 0 ? 'rgba(200, 200, 200, 0.4)' : 'rgba(200, 200, 200, 0.15)';
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
      
      // Labels de coordenadas a cada 500px
      if (x % 500 === 0 && x > 0) {
        ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
        ctx.font = '11px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`x:${x}`, x + 5, 15);
      }
    }
    
    // Linhas horizontais
    for (let y = 0; y < canvas.height; y += 100) {
      ctx.strokeStyle = y % 500 === 0 ? 'rgba(200, 200, 200, 0.4)' : 'rgba(200, 200, 200, 0.15)';
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
      
      // Labels de coordenadas a cada 500px
      if (y % 500 === 0 && y > 0) {
        ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
        ctx.font = '11px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`y:${y}`, 5, y - 5);
      }
    }
    
    // Texto indicativo da página
    ctx.fillStyle = 'rgba(150, 150, 150, 0.3)';
    ctx.font = '48px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`Página: ${pagePath}`, canvas.width / 2, canvas.height / 2);
    
  }, [dynamicHeight, pagePath]);
  
  // Desenhar heatmap com marcadores visuais individuais
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    canvas.width = container.offsetWidth;
    canvas.height = dynamicHeight;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (!data.length) return;
    
    const maxIntensity = Math.max(...data.map(p => p.intensity));
    
    data.forEach(point => {
      const normalizedIntensity = point.intensity / maxIntensity;
      
      // Gradiente de fundo maior
      const gradientRadius = 60;
      const gradient = ctx.createRadialGradient(
        point.x, point.y, 0,
        point.x, point.y, gradientRadius
      );
      
      const alpha = Math.min(normalizedIntensity * 0.6 + 0.2, 0.7);
      
      let color;
      if (normalizedIntensity < 0.25) {
        color = `rgba(0, 100, 255, ${alpha})`;
      } else if (normalizedIntensity < 0.5) {
        color = `rgba(0, 200, 100, ${alpha})`;
      } else if (normalizedIntensity < 0.75) {
        color = `rgba(255, 200, 0, ${alpha})`;
      } else {
        color = `rgba(255, 50, 50, ${alpha})`;
      }
      
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.5, color.replace(/[\d.]+\)$/, `${alpha * 0.4})`));
      gradient.addColorStop(1, color.replace(/[\d.]+\)$/, '0)'));
      
      ctx.fillStyle = gradient;
      ctx.fillRect(point.x - gradientRadius, point.y - gradientRadius, gradientRadius * 2, gradientRadius * 2);
      
      // Círculo central com borda
      ctx.beginPath();
      ctx.arc(point.x, point.y, 24, 0, 2 * Math.PI);
      ctx.fillStyle = color.replace(/[\d.]+\)$/, '0.9)');
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Número de cliques
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(point.intensity.toString(), point.x, point.y);
    });
  }, [data, dynamicHeight]);
  
  return (
    <div className="relative w-full">
      <div 
        ref={containerRef} 
        className="relative w-full bg-muted/30 rounded-lg overflow-auto border border-border"
        style={{ maxHeight: '800px' }}
      >
        {/* Canvas de fundo com grid */}
        <canvas 
          ref={backgroundCanvasRef}
          className="absolute inset-0 w-full"
          style={{ height: `${dynamicHeight}px` }}
        />
        
        {/* Canvas do heatmap */}
        <canvas 
          ref={canvasRef}
          className="relative w-full"
          style={{ 
            height: `${dynamicHeight}px`,
            mixBlendMode: 'multiply'
          }}
        />
        
        {!data.length && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <p className="text-muted-foreground">Nenhum dado de cliques disponível para esta página</p>
          </div>
        )}
      </div>
      
      {/* Legenda de Cores */}
      <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm border border-border p-4 rounded-lg shadow-lg">
        <h4 className="text-sm font-semibold mb-3">Intensidade de Cliques</h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: 'rgba(255, 50, 50, 0.8)' }}></div>
            <span>Muito clicado (75-100%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: 'rgba(255, 200, 0, 0.8)' }}></div>
            <span>Clicado (50-75%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: 'rgba(0, 200, 100, 0.8)' }}></div>
            <span>Médio (25-50%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: 'rgba(0, 100, 255, 0.8)' }}></div>
            <span>Pouco clicado (0-25%)</span>
          </div>
        </div>
      </div>
      
      {/* Guia de uso */}
      <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg max-w-xs">
        <h4 className="text-sm font-semibold mb-2">Como interpretar:</h4>
        <ul className="text-xs space-y-1 text-muted-foreground">
          <li>🔴 Círculos coloridos = áreas clicadas</li>
          <li>📊 Números nos círculos = quantidade de cliques</li>
          <li>📍 Grid com coordenadas = posição na página</li>
          <li>🎨 Gradiente ao redor = zona de influência</li>
        </ul>
      </div>
    </div>
  );
};
